import api from '../api';

/**
 * Owner-configurable inspection note presets (WP-07).
 *
 * Routes through the shared `api` axios instance (baseURL = REACT_APP_API_URL
 * + '/admin'), so paths here must NOT start with '/api' — that doubles in prod.
 * GET is staff-readable; every mutation is owner-only, enforced server-side.
 *
 * A GROUP answers "which quick values appear on this item, and what are they
 * called": the label is data, so brakes read "Wear" while exterior reads
 * "Damage Location". A VALUE optionally carries a unit and a severity that
 * drives the item's condition indicator.
 */

export type PresetScopeType = 'shop' | 'category' | 'point';
export type PresetSeverity = 'pass' | 'attention' | 'fail';

export interface DviNotePresetValue {
  id: number;
  group_id: number;
  value: string;
  unit: string | null;
  severity: PresetSeverity | null;
  sort_order: number;
  is_active: number;
}

export interface DviNotePresetGroup {
  id: number;
  shop_id: number;
  scope_type: PresetScopeType;
  /** '' for the shop-wide catch-all; otherwise a category or point name. */
  scope_value: string;
  /** The measurement label shown to the technician and the customer. */
  label: string;
  sort_order: number;
  is_active: number;
  values: DviNotePresetValue[];
}

/** All preset groups for the current shop (owner settings screen). */
export const getNotePresetGroups = async (): Promise<DviNotePresetGroup[]> => {
  const { data } = await api.get('/dvi-note-presets');
  return data;
};

/**
 * The group that applies to one inspection point — most specific wins
 * (point name, then category, then shop-wide).
 *
 * Resolves to null when the shop has configured nothing. That is a normal
 * answer, not an error: the form still offers a free-text note.
 */
export const resolveNotePresets = async (
  pointName?: string | null,
  categoryName?: string | null,
): Promise<DviNotePresetGroup | null> => {
  const { data } = await api.get('/dvi-note-presets/resolve', {
    params: {
      point_name: pointName ?? undefined,
      category_name: categoryName ?? undefined,
    },
  });
  return data ?? null;
};

export const createNotePresetGroup = async (input: {
  scope_type?: PresetScopeType;
  scope_value?: string;
  label: string;
  sort_order?: number;
}): Promise<DviNotePresetGroup> => {
  const { data } = await api.post('/dvi-note-presets', input);
  return data;
};

export const updateNotePresetGroup = async (
  id: number,
  input: { label?: string; scope_type?: PresetScopeType; scope_value?: string; sort_order?: number; is_active?: number },
): Promise<DviNotePresetGroup> => {
  const { data } = await api.put(`/dvi-note-presets/${id}`, input);
  return data;
};

export const deleteNotePresetGroup = async (id: number): Promise<void> => {
  await api.delete(`/dvi-note-presets/${id}`);
};

export const reorderNotePresetGroups = async (ids: number[]): Promise<DviNotePresetGroup[]> => {
  const { data } = await api.put('/dvi-note-presets/reorder', { ids });
  return data;
};

export const createNotePresetValue = async (
  groupId: number,
  input: { value: string; unit?: string | null; severity?: PresetSeverity | null; sort_order?: number },
): Promise<DviNotePresetValue> => {
  const { data } = await api.post(`/dvi-note-presets/${groupId}/values`, input);
  return data;
};

export const updateNotePresetValue = async (
  id: number,
  input: { value?: string; unit?: string | null; severity?: PresetSeverity | null; sort_order?: number; is_active?: number },
): Promise<DviNotePresetValue> => {
  const { data } = await api.put(`/dvi-note-presets/values/${id}`, input);
  return data;
};

/**
 * Retire a value. Soft server-side — finished inspections hold a COPY of the
 * text, so removing a preset never alters history.
 */
export const deleteNotePresetValue = async (id: number): Promise<void> => {
  await api.delete(`/dvi-note-presets/values/${id}`);
};

export const reorderNotePresetValues = async (
  groupId: number,
  ids: number[],
): Promise<DviNotePresetValue[]> => {
  const { data } = await api.put(`/dvi-note-presets/${groupId}/values/reorder`, { ids });
  return data;
};
